/**
 * AdminOrderTimeline.js — Vue "Cycle de vie" d'une commande
 * Timeline verticale 10 phases / 18 statuts.
 * Réutilise les endpoints existants (contrat, missions, facture, status_history).
 * Ne modifie aucune logique du DevisEditor.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, AlertTriangle, ExternalLink, Send,
  FileText, Package, Truck, RotateCcw, Star, CreditCard,
  User, ChevronRight, Download, Camera, X
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { openProtectedDoc } from '../utils/docLinks';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}`, 'Content-Type': 'application/json' });

// ─── 10 phases du cycle de vie ────────────────────────────────────────────────
const PHASES = [
  {
    id: 'paiement',
    label: 'Paiement',
    icon: <CreditCard size={14}/>,
    statuts: ['en_attente_paiement','paiement_partiel','payee',
              'brouillon','envoye','signe','acompte_paye','solde_paye'],
    activeStatuts: ['en_attente_paiement','paiement_partiel','brouillon','envoye','signe','acompte_paye'],
    doneStatuts:   ['payee','solde_paye'],
    description: (q) => {
      const total = parseFloat(q.total || 0);
      const paye = (typeof q._montantPaye === 'number') ? q._montantPaye : null;
      if (['payee','solde_paye'].includes(q.statut)) return `${(paye ?? total).toFixed(0)}€ encaissé ✓`;
      if (['paiement_partiel','acompte_paye','signe'].includes(q.statut)) {
        if (paye != null && paye > 0) return `${paye.toFixed(0)}€ encaissé — solde ${Math.max(total - paye, 0).toFixed(0)}€ restant`;
        return `Acompte ${(total*0.3).toFixed(0)}€ attendu — solde ${(total*0.7).toFixed(0)}€`;
      }
      return `${total.toFixed(0)}€ — en attente`;
    },
    actions: (q, handlers) => q.statut === 'paiement_partiel' || q.statut === 'acompte_paye' ? (
      <button onClick={() => handlers.sendPaymentLink(q)} style={btnStyle('var(--admin-gold)')}>
        <Send size={12}/> Envoyer lien solde
      </button>
    ) : null,
  },
  {
    id: 'contrat',
    label: 'Contrat',
    icon: <FileText size={14}/>,
    statuts: ['contrat_envoye','contrat_signe'],
    activeStatuts: ['contrat_envoye'],
    doneStatuts:   ['contrat_signe'],
    description: (q) => q.statut === 'contrat_signe' ? 'Signé ✓' : q.statut === 'contrat_envoye' ? 'Envoyé — en attente signature' : '',
    actions: (q, handlers) => (
      <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
        <button onClick={() => handlers.openDoc(`/api/contrat/${q.id}`)} data-testid={`timeline-contrat-${q.id}`} style={btnStyle('var(--admin-teal)')}>
          <ExternalLink size={12}/> Voir le contrat
        </button>
        <button onClick={() => handlers.sendContrat(q.id)} style={btnStyle('var(--admin-teal)')}>
          <Send size={12}/> Envoyer contrat
        </button>
      </div>
    ),
  },
  {
    id: 'preparation',
    label: 'Préparation',
    icon: <Package size={14}/>,
    statuts: ['a_preparer'],
    activeStatuts: ['a_preparer'],
    doneStatuts:   [],
    description: () => 'Matériel à préparer avant le chargement',
    actions: (q, handlers) => (
      <button onClick={() => handlers.openMission(q.id, 'chargement')} style={btnStyle('var(--admin-purple)')}>
        <Package size={12}/> Checklist chargement
      </button>
    ),
  },
  {
    id: 'chargement',
    label: 'Chargement',
    icon: <Package size={14}/>,
    statuts: ['produits_charges'],
    activeStatuts: ['produits_charges'],
    doneStatuts:   [],
    description: () => 'Matériel chargé, prêt à partir',
    actions: (q, handlers) => (
      <button onClick={() => handlers.openMission(q.id, 'chargement')} style={btnStyle('var(--admin-blue)')}>
        <Package size={12}/> Voir checklist
      </button>
    ),
  },
  {
    id: 'livraison',
    label: 'Livraison',
    icon: <Truck size={14}/>,
    statuts: ['en_livraison','livree'],
    activeStatuts: ['en_livraison'],
    doneStatuts:   ['livree'],
    description: (q) => {
      const sess = q.session || {};
      const h = q.heure_livraison || sess.heure_livraison || '09:00';
      const d = q.date_livraison || sess.date_livraison || sess.date_evenement || '';
      return d ? `${formatDate(d)} à ${h}` : 'Date à définir';
    },
    actions: (q, handlers) => (
      <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
        <button onClick={() => handlers.openMission(q.id, 'livraison')} style={btnStyle('var(--admin-blue)')}>
          <Camera size={12}/> Photos + Signature
        </button>
        <a href={`/mission?quote=${q.id}`} target="_blank" rel="noreferrer" style={linkStyle('var(--admin-teal)')}>
          <ExternalLink size={12}/> App terrain
        </a>
      </div>
    ),
  },
  {
    id: 'location',
    label: 'En location',
    icon: <Clock size={14}/>,
    statuts: ['en_location'],
    activeStatuts: ['en_location'],
    doneStatuts:   [],
    description: (q) => {
      const sess = q.session || {};
      const d = q.date_reprise || sess.date_reprise || '';
      return d ? `Retour prévu le ${formatDate(d)}` : 'Retour à planifier';
    },
    actions: null,
  },
  {
    id: 'retour',
    label: 'Retour',
    icon: <RotateCcw size={14}/>,
    statuts: ['retour_a_effectuer','retour_effectue','retour_anomalie'],
    activeStatuts: ['retour_a_effectuer'],
    doneStatuts:   ['retour_effectue'],
    anomalyStatuts: ['retour_anomalie'],
    description: (q) => {
      if (q.statut === 'retour_anomalie') return '⚠️ Anomalie signalée';
      if (q.statut === 'retour_effectue') return 'Retour effectué ✓';
      const sess = q.session || {};
      const h = q.heure_retour || sess.heure_retour || '10:00';
      return `Récupération prévue à ${h}`;
    },
    actions: (q, handlers) => (
      <button onClick={() => handlers.openMission(q.id, 'retour')} style={btnStyle('var(--admin-amber)')}>
        <RotateCcw size={12}/> Fiche retour + Signature
      </button>
    ),
  },
  {
    id: 'controle',
    label: 'Contrôle',
    icon: <CheckCircle size={14}/>,
    statuts: ['controle_termine'],
    activeStatuts: [],
    doneStatuts:   ['controle_termine'],
    description: () => 'État du matériel vérifié par l\'équipe',
    actions: null,
  },
  {
    id: 'facture',
    label: 'Facture',
    icon: <FileText size={14}/>,
    statuts: ['facture_envoyee'],
    activeStatuts: ['facture_envoyee'],
    doneStatuts:   [],
    description: (q) => q.facture_envoyee_at ? `Envoyée le ${formatDate(q.facture_envoyee_at)}` : 'À envoyer',
    actions: (q, handlers) => (
      <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
        <button onClick={() => handlers.openDoc(`/api/devis/${q.id}/facture`)} data-testid={`timeline-facture-${q.id}`} style={btnStyle('var(--admin-gold)')}>
          <Download size={12}/> Télécharger PDF
        </button>
        <button onClick={() => handlers.sendFacture(q.id)} style={btnStyle('var(--admin-gold)')}>
          <Send size={12}/> Renvoyer
        </button>
      </div>
    ),
  },
  {
    id: 'avis',
    label: 'Avis',
    icon: <Star size={14}/>,
    statuts: ['avis_demande','terminee','termine'],
    activeStatuts: ['avis_demande'],
    doneStatuts:   ['terminee','termine'],
    description: () => 'Demande d\'avis envoyée au client',
    actions: null,
  },
];

// ─── Statut « d'entrée » de chaque phase (validation manuelle admin) ───────────
const PHASE_ENTRY = {
  paiement:    'paiement_partiel',
  contrat:     'contrat_signe',
  preparation: 'a_preparer',
  chargement:  'produits_charges',
  livraison:   'livree',
  location:    'en_location',
  retour:      'retour_effectue',
  controle:    'controle_termine',
  facture:     'facture_envoyee',
  avis:        'terminee',
};

// ─── Étapes qui envoient un email automatique au client (choix à la validation) ─
const EMAIL_STEPS = ['paiement', 'contrat', 'livraison', 'retour', 'facture', 'avis'];
const EMAIL_STEP_LABELS = {
  paiement: 'confirmation de paiement', contrat: 'contrat', livraison: 'confirmation de livraison',
  retour: 'confirmation de retour', facture: 'facture (PDF joint)', avis: "demande d'avis",
};

// ─── Styles helpers ────────────────────────────────────────────────────────────
function btnStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 9, fontSize: 12, fontWeight: 600,
    background: `${color}18`, color, border: 'none', cursor: 'pointer',
  };
}
function linkStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 9, fontSize: 12, fontWeight: 600,
    background: `${color}18`, color, textDecoration: 'none',
  };
}

// ─── Calculer l'index de phase actuelle ───────────────────────────────────────
function getPhaseStatus(phase, currentStatut) {
  const allDone    = [...(phase.doneStatuts || [])];
  const allAnomaly = [...(phase.anomalyStatuts || [])];
  const allStatuts = [...(phase.statuts || [])];

  // Trouver la position de la phase actuelle dans le workflow global
  const phaseIdx = PHASES.findIndex(p => p.id === phase.id);
  const currentPhaseIdx = PHASES.findIndex(p =>
    [...(p.statuts || [])].includes(currentStatut)
  );

  if (allAnomaly.includes(currentStatut) && phase.id === 'retour') return 'anomaly';
  if (allStatuts.includes(currentStatut)) {
    return allDone.includes(currentStatut) ? 'done' : 'active';
  }
  if (phaseIdx < currentPhaseIdx) return 'done';
  return 'todo';
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function AdminOrderTimeline({ quote, onStatusChange }) {
  const [history,    setHistory]    = useState([]);
  const [loadingH,   setLoadingH]   = useState(false);
  const [sending,    setSending]    = useState({});
  const [confirmStep, setConfirmStep] = useState(null);   // {idx} de l'étape à valider (email)
  const [sendStepEmail, setSendStepEmail] = useState(false);
  const [montantPaye, setMontantPaye] = useState(null);   // montant réellement encaissé

  const quoteId = quote?.id;

  useEffect(() => {
    if (!quoteId) return;
    setLoadingH(true);
    fetch(`${BACKEND}/api/booking/order/${quoteId}/status-history`, { headers: H() })
      .then(r => r.json())
      .then(d => setHistory(d.history || []))
      .catch(() => {})
      .finally(() => setLoadingH(false));
    fetch(`${BACKEND}/api/devis/${quoteId}/montant-paye`, { headers: H() })
      .then(r => r.json())
      .then(d => { if (typeof d.montant_paye === 'number') setMontantPaye(d.montant_paye); })
      .catch(() => {});
  }, [quoteId]);

  if (!quote) return null;

  const sess    = quote.session || {};
  const addrLiv = quote.adresse_livraison || sess.adresse || '';
  const addrFac = quote.adresse_facturation || '';
  const dateEv  = quote.date_livraison || sess.date_livraison || sess.date_evenement || '';

  // ── Actions contextuelles ──────────────────────────────────────────────────
  async function sendPaymentLink(q) {
    setSending(p => ({...p, paylink: true}));
    try {
      const res = await fetch(`${BACKEND}/api/devis/${q.id}/envoyer-lien-solde`, { method: 'POST', headers: H() });
      const d = await res.json();
      toast.success(d.message || 'Lien envoyé');
    } catch { toast.error('Erreur'); }
    setSending(p => ({...p, paylink: false}));
  }

  async function sendContrat(id) {
    setSending(p => ({...p, contrat: true}));
    try {
      const res = await fetch(`${BACKEND}/api/contrat/${id}/envoyer`, { method: 'POST', headers: H() });
      const d = await res.json();
      toast.success(d.message || 'Contrat envoyé');
      if (onStatusChange) onStatusChange();
    } catch { toast.error('Erreur'); }
    setSending(p => ({...p, contrat: false}));
  }

  async function sendFacture(id) {
    setSending(p => ({...p, facture: true}));
    try {
      await fetch(`${BACKEND}/api/admin/documents/${id}/renvoyer/facture`, { method: 'POST', headers: H() });
      toast.success('Facture renvoyée');
    } catch { toast.error('Erreur'); }
    setSending(p => ({...p, facture: false}));
  }

  function openMission(id, type) {
    window.open(`/mission?quote=${id}&type=${type}`, '_blank');
  }

  // ANO-ECC-04 : téléchargement authentifié (Bearer admin) — plus de lien direct par id
  async function openDoc(path) {
    const ok = await openProtectedDoc(BACKEND, path, TOKEN());
    if (!ok) toast.error('Lien expiré ou accès refusé. Reconnectez-vous puis réessayez.');
  }

  const handlers = { sendPaymentLink, sendContrat, sendFacture, openMission, openDoc };

  // ── Validation manuelle des étapes (avance / recule le cycle de vie) ────────
  async function setPhaseStatus(newStatut, label) {
    setSending(p => ({ ...p, phase: true }));
    try {
      const res = await fetch(`${BACKEND}/api/booking/order/${quoteId}/status`, {
        method: 'PUT', headers: H(),
        body: JSON.stringify({ statut: newStatut, changed_by: 'admin', note: 'Validation manuelle' }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Étape validée : ${label}`);
      fetch(`${BACKEND}/api/booking/order/${quoteId}/status-history`, { headers: H() })
        .then(r => r.json()).then(d => setHistory(d.history || [])).catch(() => {});
      if (onStatusChange) onStatusChange();
    } catch { toast.error('Erreur lors de la validation'); }
    setSending(p => ({ ...p, phase: false }));
  }
  function goBackPhase(idx) {
    if (idx <= 0) return;
    const prev = PHASES[idx - 1];
    setPhaseStatus(PHASE_ENTRY[prev.id], prev.label);
  }

  // ── Valider / Refuser la RÉSERVATION (décision admin, pas le client) ────────
  async function validerReservation() {
    setSending(p => ({ ...p, resa: true }));
    try {
      const res = await fetch(`${BACKEND}/api/admin/reservations/${quoteId}/valider`, {
        method: 'POST', headers: H(), body: JSON.stringify({ notes: '' }),
      });
      if (!res.ok) throw new Error();
      toast.success('Réservation validée ✓ — client notifié, matériel bloqué');
      if (onStatusChange) onStatusChange();
    } catch { toast.error('Erreur lors de la validation'); }
    setSending(p => ({ ...p, resa: false }));
  }
  async function refuserReservation() {
    const raison = window.prompt('Motif du refus (transmis au client par email) :', '');
    if (raison === null) return;
    setSending(p => ({ ...p, resa: true }));
    try {
      const res = await fetch(`${BACKEND}/api/admin/reservations/${quoteId}/refuser`, {
        method: 'POST', headers: H(), body: JSON.stringify({ raison }),
      });
      if (!res.ok) throw new Error();
      toast.success('Réservation refusée — client notifié, matériel libéré');
      if (onStatusChange) onStatusChange();
    } catch { toast.error('Erreur'); }
    setSending(p => ({ ...p, resa: false }));
  }
  const needsValidation = ['acompte_paye', 'signe'].includes(quote.statut);

  // ── Étape sur laquelle afficher les boutons de validation manuelle ──────────
  const isCancelled   = ['annulee','annule','refuse','refusee','expire','expiree'].includes(quote.statut);
  const activeIdx     = PHASES.findIndex(p => getPhaseStatus(p, quote.statut) === 'active');
  let lastDoneIdx = -1;
  PHASES.forEach((p, i) => { if (getPhaseStatus(p, quote.statut) === 'done') lastDoneIdx = i; });
  const orderComplete = getPhaseStatus(PHASES[PHASES.length - 1], quote.statut) === 'done';
  const actionIdx     = activeIdx >= 0 ? activeIdx : Math.min(lastDoneIdx + 1, PHASES.length - 1);
  const showValidate  = !isCancelled && !orderComplete;

  // ── Valider une étape en cliquant dessus (valide aussi les précédentes) ──────
  function openStepConfirm(idx) {
    if (isCancelled || sending.phase) return;
    const phase = PHASES[idx];
    if (EMAIL_STEPS.includes(phase.id)) {
      setSendStepEmail(false);
      setConfirmStep({ idx });
    } else {
      doValidateStep(idx, false);
    }
  }
  async function doValidateStep(idx, sendEmail) {
    const phase = PHASES[idx];
    // Valider l'étape idx => marquer idx (et toutes les précédentes) comme faites
    const target = idx < PHASES.length - 1 ? (PHASE_ENTRY[PHASES[idx + 1].id] || 'terminee') : 'terminee';
    await setPhaseStatus(target, phase.label);
    if (sendEmail && EMAIL_STEPS.includes(phase.id)) {
      try {
        const res = await fetch(`${BACKEND}/api/devis/${quoteId}/etape-email/${phase.id}`, { method: 'POST', headers: H() });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.success) toast.success(`Email « ${EMAIL_STEP_LABELS[phase.id]} » envoyé au client`);
        else if (d.skipped) toast.info('Email non envoyé (client de test / sans email)');
        else toast.error("Échec de l'envoi de l'email");
      } catch { toast.error("Échec de l'envoi de l'email"); }
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'var(--admin-font-sans)' }}>

      {/* ── Commande à valider (décision admin) ── */}
      {needsValidation && (
        <div className="adm-card" data-testid="timeline-validation-banner"
          style={{ padding: '14px 16px', marginBottom: 16, background: 'rgba(249,115,22,0.10)',
            border: '1px solid rgba(249,115,22,0.35)' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#F97316', marginBottom: 3 }}>
            ⏳ Commande à valider
          </p>
          <p style={{ fontSize: 12, color: 'var(--admin-muted)', marginBottom: 10, lineHeight: 1.5 }}>
            Le client a payé. C'est à VOUS de confirmer (ou refuser) la réservation — le client ne confirme pas seul.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={validerReservation} disabled={sending.resa}
              data-testid="timeline-valider-resa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                background: 'var(--admin-teal)', color: '#0B1E20' }}>
              <CheckCircle size={14}/> Valider la réservation
            </button>
            <button onClick={refuserReservation} disabled={sending.resa}
              data-testid="timeline-refuser-resa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                background: 'transparent', color: '#D94B5D', border: '1px solid #D94B5D' }}>
              <X size={14}/> Refuser
            </button>
          </div>
        </div>
      )}

      {/* ── En-tête : adresses + lien client ── */}
      <div className="adm-card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--admin-teal)', fontWeight: 700, marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 4 }}>
              <Truck size={11}/> Livraison (lieu de l'événement)
            </p>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)', lineHeight: 1.4 }}>
              {addrLiv || <em style={{ color:'var(--admin-faint)' }}>Non renseignée</em>}
            </p>
            {dateEv && (
              <p style={{ fontSize: 11.5, color: 'var(--admin-muted)', marginTop: 3 }}>
                📅 {formatDate(dateEv)}
              </p>
            )}
          </div>
          <div>
            <p style={{ fontSize: 11, color: 'var(--admin-gold)', fontWeight: 700, marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 4 }}>
              <FileText size={11}/> Facturation (payeur)
            </p>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)', lineHeight: 1.4 }}>
              {addrFac || <em style={{ color:'var(--admin-faint)' }}>Identique à la livraison</em>}
            </p>
          </div>
        </div>
        {/* Lien client */}
        {quote.client_nom && (
          <div style={{ paddingTop: 10, borderTop: '1px solid var(--admin-border-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <User size={13} style={{ color: 'var(--admin-teal)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>{quote.client_nom}</span>
              {quote.client_telephone && (
                <a href={`tel:${quote.client_telephone}`}
                  style={{ fontSize: 12, color: 'var(--admin-teal)', textDecoration: 'none' }}>
                  📞 {quote.client_telephone}
                </a>
              )}
            </div>
            <Link to={`/admin/clients`}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
                color: 'var(--admin-muted)', textDecoration: 'none' }}>
              Fiche client <ChevronRight size={12}/>
            </Link>
          </div>
        )}
      </div>

      {/* ── Timeline verticale 10 phases ── */}
      <div style={{ position: 'relative', paddingLeft: 6 }}>
        {/* Barre verticale de connexion */}
        <div style={{
          position: 'absolute', left: 19, top: 12, bottom: 12, width: 2,
          background: 'var(--admin-border-soft)', zIndex: 0,
        }} />

        {PHASES.map((phase, idx) => {
          const status = getPhaseStatus(phase, quote.statut);
          const isDone    = status === 'done';
          const isActive  = status === 'active';
          const isAnomaly = status === 'anomaly';
          const isTodo    = status === 'todo';

          const nodeColor = isDone ? 'var(--admin-teal)'
            : isActive  ? 'var(--admin-gold)'
            : isAnomaly ? 'var(--admin-red)'
            : 'var(--admin-border)';

          const nodeBg = isDone ? 'var(--admin-teal)'
            : isActive  ? 'var(--admin-gold)'
            : isAnomaly ? 'var(--admin-red)'
            : 'var(--admin-surface2)';

          const textColor = isDone || isActive || isAnomaly ? 'var(--admin-text)' : 'var(--admin-faint)';

          // Historique pour cette phase
          const phaseHistory = history.filter(h =>
            phase.statuts.includes(h.new_status)
          );

          return (
            <div key={phase.id} style={{ display: 'flex', gap: 14, paddingBottom: 4, position: 'relative', zIndex: 1 }}>
              {/* Nœud + barre */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                {/* Nœud rond (cliquable pour valider l'étape) */}
                <div onClick={() => openStepConfirm(idx)}
                  title={isCancelled ? '' : 'Cliquer pour valider cette étape (et les précédentes)'}
                  data-testid={`phase-node-${phase.id}`}
                  style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: nodeBg,
                  border: `2px solid ${nodeColor}`,
                  zIndex: 2,
                  cursor: isCancelled ? 'default' : 'pointer',
                  boxShadow: isActive ? `0 0 0 5px ${nodeBg}30` : 'none',
                  animation: isActive ? 'adm-pulse 2s ease-in-out infinite' : 'none',
                  color: isDone || isActive || isAnomaly ? '#fff' : 'var(--admin-faint)',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {isDone ? <CheckCircle size={13}/> : isAnomaly ? <AlertTriangle size={13}/> : idx + 1}
                </div>
                {/* Connecteur bas */}
                {idx < PHASES.length - 1 && (
                  <div style={{
                    width: 2, flex: 1, minHeight: 18,
                    background: isDone ? 'var(--admin-teal-line)' : 'var(--admin-border-soft)',
                    marginTop: 2,
                  }} />
                )}
              </div>

              {/* Contenu de l'étape */}
              <div style={{ paddingBottom: idx < PHASES.length - 1 ? 18 : 4, flex: 1, minWidth: 0 }}>
                {/* Titre + icône (cliquable pour valider l'étape) */}
                <div onClick={() => openStepConfirm(idx)}
                  title={isCancelled ? '' : 'Cliquer pour valider cette étape (et les précédentes)'}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                    cursor: isCancelled ? 'default' : 'pointer' }}>
                  <span style={{ color: nodeColor }}>{phase.icon}</span>
                  <p style={{ fontSize: 14, fontWeight: isActive || isDone ? 700 : 500, color: textColor }}>
                    {phase.label}
                    {isActive && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px',
                        borderRadius: 20, background: 'var(--admin-gold)', color: '#2a1d05' }}>
                        EN COURS
                      </span>
                    )}
                    {isAnomaly && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px',
                        borderRadius: 20, background: 'var(--admin-red)', color: '#fff' }}>
                        ANOMALIE
                      </span>
                    )}
                  </p>
                </div>

                {/* Description */}
                {(isActive || isDone || isAnomaly || idx === actionIdx) && (
                  <p style={{ fontSize: 12.5, color: 'var(--admin-muted)', lineHeight: 1.5, marginBottom: 6 }}>
                    {phase.description(phase.id === 'paiement' ? { ...quote, _montantPaye: montantPaye } : quote)}
                  </p>
                )}

                {/* Historique de l'étape */}
                {phaseHistory.length > 0 && (
                  <p style={{ fontSize: 11, color: 'var(--admin-faint)', marginBottom: 6, fontStyle: 'italic' }}>
                    {formatDate(phaseHistory[phaseHistory.length-1]?.created_at)} — {phaseHistory[phaseHistory.length-1]?.changed_by || 'système'}
                  </p>
                )}

                {/* Actions contextuelles */}
                {(isActive || idx === actionIdx || (isDone && phase.id === 'contrat') || (isDone && phase.id === 'facture')) && phase.actions && (
                  <div style={{ marginTop: 6 }}>
                    {phase.actions(quote, handlers)}
                  </div>
                )}

                {/* Validation manuelle (avancer / reculer le cycle de vie) */}
                {showValidate && idx === actionIdx && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => openStepConfirm(idx)} disabled={sending.phase}
                      data-testid={`phase-validate-${phase.id}`} style={btnStyle('var(--admin-teal)')}>
                      <CheckCircle size={12}/> {idx < PHASES.length - 1 ? `Valider : ${phase.label}` : 'Marquer terminé'}
                    </button>
                    {(lastDoneIdx >= 0 || activeIdx > 0) && (
                      <button onClick={() => goBackPhase(idx)} disabled={sending.phase}
                        data-testid={`phase-back-${phase.id}`} style={btnStyle('var(--admin-faint)')}>
                        ◀ Étape précédente
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Historique complet ── */}
      {history.length > 0 && (
        <div className="adm-card" style={{ padding: '13px 15px', marginTop: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--admin-muted)', marginBottom: 10,
            textTransform: 'uppercase', letterSpacing: '.08em' }}>
            Historique des statuts
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...history].reverse().slice(0, 8).map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--admin-teal)', flexShrink: 0 }} />
                <span style={{ color: 'var(--admin-text)', fontWeight: 600 }}>{h.new_status?.replace(/_/g, ' ')}</span>
                <span style={{ color: 'var(--admin-faint)', marginLeft: 'auto' }}>{formatDate(h.created_at)}</span>
                {h.changed_by && <span style={{ color: 'var(--admin-faint)' }}>· {h.changed_by}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Confirmation de validation d'étape (avec choix d'email) ── */}
      {confirmStep !== null && (() => {
        const ph = PHASES[confirmStep.idx];
        return (
          <div onClick={() => setConfirmStep(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 60,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="adm-card" onClick={e => e.stopPropagation()} data-testid="step-confirm-modal"
              style={{ maxWidth: 400, width: '100%', padding: 20 }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--admin-text)', marginBottom: 6 }}>
                Valider l'étape « {ph.label} » ?
              </p>
              <p style={{ fontSize: 12.5, color: 'var(--admin-muted)', marginBottom: 14, lineHeight: 1.5 }}>
                Cette étape et toutes les précédentes seront marquées comme validées.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer',
                padding: '10px 12px', borderRadius: 9, background: 'var(--admin-surface)' }}>
                <input type="checkbox" checked={sendStepEmail} data-testid="step-email-checkbox"
                  onChange={e => setSendStepEmail(e.target.checked)} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, color: 'var(--admin-text)' }}>
                  Envoyer l'email au client ({EMAIL_STEP_LABELS[ph.id]})
                </span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button data-testid="step-confirm-validate"
                  onClick={() => { const idx = confirmStep.idx; const se = sendStepEmail; setConfirmStep(null); doValidateStep(idx, se); }}
                  style={{ flex: 1, ...btnStyle('var(--admin-teal)'), justifyContent: 'center', padding: '10px 16px' }}>
                  <CheckCircle size={13}/> Valider
                </button>
                <button data-testid="step-confirm-cancel" onClick={() => setConfirmStep(null)}
                  style={{ flex: 1, ...btnStyle('var(--admin-faint)'), justifyContent: 'center', padding: '10px 16px' }}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
